import React, { useState } from 'react';
import { useWaterStore } from '../store/useWaterStore';
import { Coffee, Droplets, Wine, CupSoda, GlassWater } from 'lucide-react';

const DRINK_TYPES = [
  { id: 'water', name: 'Water', multiplier: 1.0, icon: Droplets, color: 'bg-gradient-to-b from-blue-300 to-blue-500', rgb: [59, 130, 246] },
  { id: 'coffee', name: 'Coffee', multiplier: 0.9, icon: Coffee, color: 'bg-gradient-to-b from-amber-700 to-amber-900', rgb: [120, 53, 15] },
  { id: 'tea', name: 'Tea', multiplier: 0.9, icon: GlassWater, color: 'bg-gradient-to-b from-emerald-400 to-emerald-600', rgb: [16, 185, 129] },
  { id: 'soda', name: 'Soda', multiplier: 0.8, icon: CupSoda, color: 'bg-gradient-to-b from-red-400 to-red-600', rgb: [239, 68, 68] },
  { id: 'juice', name: 'Juice', multiplier: 0.9, icon: Wine, color: 'bg-gradient-to-b from-orange-400 to-orange-600', rgb: [249, 115, 22] },
];

export const WaterBottle: React.FC = () => {
  const { currentAmount, dailyGoal, addWater, drinkHistory } = useWaterStore();
  const [selectedDrink, setSelectedDrink] = useState(DRINK_TYPES[0]);

  // Ensure goal isn't zero to prevent divide by zero
  const percentage = dailyGoal > 0 ? (currentAmount / dailyGoal) * 100 : 0;

  const handleAddDrink = () => {
    addWater(250, selectedDrink.multiplier, { name: selectedDrink.name, color: selectedDrink.color }); // Add 250ml standard cup
  };

  // Calculate mixed color
  const mixedColor = React.useMemo(() => {
    if (drinkHistory.length === 0) return 'rgb(59, 130, 246)'; // Default blue

    let totalR = 0;
    let totalG = 0;
    let totalB = 0;
    let totalVolume = 0;

    drinkHistory.forEach(drink => {
      const drinkType = DRINK_TYPES.find(d => d.name === drink.name);
      if (drinkType && drinkType.rgb) {
        totalR += drinkType.rgb[0] * drink.amount;
        totalG += drinkType.rgb[1] * drink.amount;
        totalB += drinkType.rgb[2] * drink.amount;
        totalVolume += drink.amount;
      } else {
        // Fallback to water blue if type not found
        totalR += 59 * drink.amount;
        totalG += 130 * drink.amount;
        totalB += 246 * drink.amount;
        totalVolume += drink.amount;
      }
    });

    if (totalVolume === 0) return 'rgb(59, 130, 246)';

    const r = Math.round(totalR / totalVolume);
    const g = Math.round(totalG / totalVolume);
    const b = Math.round(totalB / totalVolume);

    return `rgb(${r}, ${g}, ${b})`;
  }, [drinkHistory]);

  // Calculate soda intake
  const sodaIntake = React.useMemo(() => {
    return drinkHistory
      .filter(d => d.name === 'Soda')
      .reduce((acc, curr) => acc + curr.amount, 0);
  }, [drinkHistory]);

  const showSodaWarning = sodaIntake > 500; // Warning threshold: > 500ml (more than 2 cups)

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-36 h-96 mx-auto mb-6">
        {/* Bottle Cap - White to match reference */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-10 h-4 bg-white rounded-md z-10 border-b-2 border-gray-500">
          {/* Cap grip lines */}
          <div className="absolute top-1 left-0 w-full h-px bg-gray-200/50"></div>
          <div className="absolute top-2 left-0 w-full h-px bg-gray-200/50"></div>
        </div>

        {/* Main bottle body */}
        {/* Starts at top-5 to leave a 1.25rem "neck" gap below the cap */}
        <div className="absolute top-5 w-full h-[calc(100%-1.25rem)]">
          {/* Container for bottle shape and water */}
          {/* Increased rounded-t value for even more rounded cone top, and darker border */}
          <div className="relative w-full h-full rounded-t-[4rem] rounded-b-xl overflow-hidden border-2 border-gray-500 bg-gradient-to-b from-white/30 to-white/10 backdrop-blur-sm">

            {/* Bottle glass effect highlights */}
            <div className="absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-transparent to-white/20"></div>
            <div className="absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-transparent to-white/20"></div>

            {/* Classic plastic bottle ridges - positions adjusted for better fit with new top curve */}
            <div className="absolute top-1/3 left-0 w-full h-1.5 bg-black/5 border-t border-b border-white/20 z-10"></div>
            <div className="absolute top-2/3 left-0 w-full h-1.5 bg-black/5 border-t border-b border-white/20 z-10"></div>

            {/* Water fill - Mixed Color */}
            <div
              className="absolute bottom-0 w-full transition-all duration-1000 ease-in-out"
              style={{
                height: `${Math.min(percentage, 100)}%`,
                backgroundColor: mixedColor,
                boxShadow: 'inset 0 0 20px rgba(0,0,0,0.2)' // Add inner shadow for volume
              }}
            >
              {/* Gradient overlay for volume */}
              <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-black/10 pointer-events-none"></div>
            </div>

            {/* Bubbles and Waves Overlay - Positioned to match total height */}
            <div
              className="absolute bottom-0 w-full pointer-events-none transition-all duration-1000 ease-in-out overflow-hidden"
              style={{ height: `${Math.min(percentage, 100)}%` }}
            >
              {/* Water surface reflection */}
              <div className="absolute top-0 left-0 w-full h-4 bg-gradient-to-b from-white/30 to-transparent z-20"></div>

              {/* Water waves */}
              <div className="absolute top-0 left-0 w-full h-2 bg-white/30 opacity-50 animate-wave z-20"></div>
              <div className="absolute top-1 left-0 w-full h-2 bg-white/20 opacity-30 animate-wave-delayed z-20"></div>

              {/* Bubble animations */}
              <div className="absolute bottom-0 left-1/4 w-2 h-2 bg-white/40 rounded-full animate-bubble z-10"></div>
              <div className="absolute bottom-0 left-2/4 w-1.5 h-1.5 bg-white/40 rounded-full animate-bubble-delayed z-10"></div>
              <div className="absolute bottom-0 left-3/4 w-1 h-1 bg-white/40 rounded-full animate-bubble z-10"></div>
              <div className="absolute bottom-4 left-1/3 w-1 h-1 bg-white/30 rounded-full animate-bubble-delayed z-10"></div>
              <div className="absolute bottom-2 left-2/3 w-2 h-2 bg-white/30 rounded-full animate-bubble z-10"></div>
            </div>
          </div>
        </div>

        {/* Percentage display */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-2xl font-bold text-blue-900 bg-white/80 px-3 py-1 rounded-full shadow-lg" style={{ zIndex: 20 }}>
          {Math.round(percentage)}%
        </div>
      </div>

      {/* Soda Warning */}
      {showSodaWarning && (
        <div className="mb-4 px-4 py-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 animate-pulse">
          <span className="text-xl">⚠️</span>
          <div className="text-sm font-medium">
            High sugar intake detected!
            <div className="text-xs font-normal opacity-80">Try switching to water for your next drink.</div>
          </div>
        </div>
      )}

      {/* Drink Controls */}
      <div className="w-full max-w-xs bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 text-center">Log a Drink (250ml)</h3>

        <div className="flex justify-center gap-2 mb-4">
          {DRINK_TYPES.map((drink) => (
            <button
              key={drink.id}
              onClick={() => setSelectedDrink(drink)}
              className={`p-2 rounded-lg transition-all ${selectedDrink.id === drink.id
                ? `${drink.color} text-white shadow-md scale-110`
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              title={`${drink.name} (${drink.multiplier}x hydration)`}
            >
              <drink.icon size={20} />
            </button>
          ))}
        </div>

        <button
          onClick={handleAddDrink}
          className={`w-full py-2 rounded-lg font-medium text-white transition-colors shadow-md ${selectedDrink.color} hover:opacity-90`}
        >
          Add {selectedDrink.name}
        </button>
        <p className="text-xs text-center text-gray-400 mt-2">
          Hydration: {Math.round(250 * selectedDrink.multiplier)}ml
        </p>
      </div>
    </div>
  );
};